import { sdk } from "@gc/ipecs-web-sdk";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useNavigate,
} from "react-router-dom";
import PWA from "./components/PWA";
import AppShell from "./components/shell/AppShell";
import config from "./config";
import { useAuth } from "./contexts/AuthContext";
import { CallDataProvider } from "./contexts/CallDataContext";
import { CallLogsProvider } from "./contexts/CallLogsContext";
import { CallsProvider } from "./contexts/CallsContext";
import { ChatProvider } from "./contexts/ChatContext";
import { DevicesProvider } from "./contexts/DevicesContext";
import { MyPresenceProvider } from "./contexts/MyPresenceContext";
import { MeetNowProvider } from "./components/primum/MeetNowButton";
import { PhoneControlProvider } from "./contexts/PhoneControlContext";
import { PresenceProvider } from "./contexts/PresenceContext";
import { SoftphoneProvider } from "./contexts/SoftphoneContext";
import { TopbarProvider } from "./contexts/TopbarContext";
import { UserClisProvider } from "./contexts/UserClisContext";
import useMessages from "./hooks/useMessages";
import { setLaunchNavigate } from "./launchHandler";
import ApproveOrder from "./screens/ApproveOrder";
import Apps from "./screens/Apps";
import Calendar from "./screens/Calendar";
import Calls from "./screens/Calls";
import ChatRoom from "./screens/ChatRoom";
import Clockify from "./screens/Clockify";
import ConfigChecker from "./screens/ConfigChecker";
import Contacts from "./screens/Contacts";
import EngageChat from "./screens/EngageChat";
import Insights from "./screens/Insights";
import IpecsLogin from "./screens/IpecsLogin";
import Login from "./screens/Login";
import MakeCall from "./screens/MakeCall";
import Messages from "./screens/Messages";
import NotFound from "./screens/NotFound";
import Projects from "./screens/Projects";
import Settings from "./screens/Settings";
import Setup from "./screens/Setup";
import Sidekick from "./screens/Sidekick";
import SimpleMakeCall from "./screens/SimpleMakeCall";
import WhatsNew from "./screens/WhatsNew";
import WhatsappThread from "./screens/WhatsappThread";

const style = {
  wrapper: {
    minHeight: "100%",
  },
};

const AppRoutes = () => {
  const { apiUser, ipecsUser, hasFeature, hasIntegration } = useAuth();

  const unauthenticatedRoutes = () => {
    return (
      <>
        <Route path="/" element={<Login />} />
        <Route path="/config-checker" element={<ConfigChecker />} />
        <Route path="*" element={<Navigate to="/" />} />
      </>
    );
  };

  const authenticatedWithoutIpecsRoutes = () => {
    return (
      <>
        <Route path="/" element={<IpecsLogin />} />
        <Route path="*" element={<IpecsLogin />} />
      </>
    );
  };

  const basicAuthenticatedRoutes = () => {
    return (
      <Route path="/" element={<BasicAuthLayout />}>
        <Route path="/simple-call/:number" element={<SimpleMakeCall />} />
      </Route>
    );
  };

  const authenticatedRoutes = () => {
    return (
      <Route path="/" element={<AuthLayout />}>
        <Route path="/pwa" element={<PWA />} />
        <Route index element={<Contacts />} />
        <Route path="/make-call/:number" element={<MakeCall />} />
        <Route
          path="/login/vincere/callback"
          Component={() => {
            window.location.href =
              config.api_url +
              "/login/vincere/callback" +
              window.location.search;
          }}
        />
        <Route path="/calls" element={<Calls />} />
        {hasIntegration(["microsoft", "google"]) && (
          <Route path="/calendar" element={<Calendar />} />
        )}
        {hasIntegration(["asana", "clickup"]) && (
          <Route path="/projects" element={<Projects />} />
        )}
        {hasIntegration(["clockify"]) && (
          <Route path="/clockify" element={<Clockify />} />
        )}
        {hasFeature(["insights"]) && (
          <Route path="/insights" element={<Insights />} />
        )}
        {apiUser?.has_sidekick && apiUser?.sidekick_configured && (
          <Route path="/sidekick/*" element={<Sidekick />} />
        )}
        <Route path="/apps" element={<Apps />} />
        <Route path="/settings/:action?" element={<Settings />} />
        <Route path="/whats-new" element={<WhatsNew />} />
        <Route path="/messages" element={<MessagesRoute />} />
        <Route path="/messages/chat/:id" element={<ChatRoom />} />
        <Route path="/messages/whatsapp/:id" element={<WhatsappThread />} />
        <Route path="/messages/engage/:id" element={<EngageChat />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    );
  };

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Wrapper />}>
          {!apiUser && unauthenticatedRoutes()}
          {apiUser && !ipecsUser && authenticatedWithoutIpecsRoutes()}
          {apiUser && ipecsUser && authenticatedRoutes()}
          {apiUser && ipecsUser && basicAuthenticatedRoutes()}
          <Route
            path="/partner/:billingPartnerId/orders/new"
            element={<Setup />}
          />
          <Route path="/order-approved" element={<ApproveOrder />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

function Wrapper() {
  const navigate = useNavigate();

  useEffect(() => {
    setLaunchNavigate(navigate);
    return () => setLaunchNavigate(null);
  }, [navigate]);

  return (
    <div style={style.wrapper}>
      <Outlet />
    </div>
  );
}

function BasicAuthLayout() {
  const { isPhoneControl } = useAuth();

  return (
    <DevicesProvider>
      <CallLogsProvider>
        <CallDataProvider>
          <MeetNowProvider>
            <CallHandler>
              <CallsProvider phoneControlMode={isPhoneControl}>
                <div>
                  <Outlet />
                </div>
              </CallsProvider>
            </CallHandler>
          </MeetNowProvider>
        </CallDataProvider>
      </CallLogsProvider>
    </DevicesProvider>
  );
}

function AuthLayout() {
  const { isPhoneControl, apiUser } = useAuth();
  const { user, setLogLevel } = sdk;

  useEffect(() => {
    if (Number.isInteger(apiUser.sdk_log_level)) {
      setLogLevel(apiUser.sdk_log_level);
    }
  }, [setLogLevel, apiUser]);

  useQuery({
    queryKey: ["auth-check"],
    queryFn: () => user.getMyInfo(),
    refetchOnWindowFocus: true,
    retry: 0,
    refetchInterval: 60000,
    cacheTime: 20000,
    staleTime: 20000,
    onError: () => window.location.reload(),
  });

  return (
    <MyPresenceProvider>
      <PresenceProvider>
        <DevicesProvider>
          <CallLogsProvider>
            <UserClisProvider>
              <CallDataProvider>
                <MeetNowProvider>
                  <CallHandler>
                    <CallsProvider phoneControlMode={isPhoneControl}>
                      <ChatProvider>
                        <AuthContent />
                      </ChatProvider>
                    </CallsProvider>
                  </CallHandler>
                </MeetNowProvider>
              </CallDataProvider>
            </UserClisProvider>
          </CallLogsProvider>
        </DevicesProvider>
      </PresenceProvider>
    </MyPresenceProvider>
  );
}

function CallHandler({ children }) {
  const { isPhoneControl } = useAuth();

  if (isPhoneControl) {
    return <PhoneControlProvider>{children}</PhoneControlProvider>;
  } else {
    return <SoftphoneProvider>{children}</SoftphoneProvider>;
  }
}

function AuthContent() {
  return (
    <TopbarProvider>
      <AppShell>
        <Outlet />
      </AppShell>
    </TopbarProvider>
  );
}

const MessagesRoute = () => {
  const { hasAnyMessageFeatures } = useMessages();

  if (!hasAnyMessageFeatures) {
    return <Navigate to="/" />;
  }

  return <Messages />;
};

export default AppRoutes;
